import { useEffect } from "react";

import { usePubNub } from "pubnub-react";

import type { MessageEnvelope } from "@pubnub/react-chat-components";

interface Props {
  onNewChannelMessage: (message: MessageEnvelope) => void;
  onNewFileMessage: (message: MessageEnvelope) => void;
  channels: string[];
}

export function usePubNubChannelListener(props: Props) {
  const { onNewChannelMessage, channels, onNewFileMessage } = props;
  const pubnubClient = usePubNub();
  // Gate the effect on the actual channel set, not the array reference.
  // Callers commonly pass an inline literal (e.g. `[currentChannel]`) which
  // produces a new reference every render and would otherwise tear down and
  // recreate the PubNub long-poll on every render of the parent.
  const channelsKey = [...channels].sort().join("|");

  useEffect(() => {
    const subscribedChannels =
      channelsKey.length === 0 ? [] : channelsKey.split("|");
    const listener = {
      message: (message: MessageEnvelope) => {
        onNewChannelMessage(message);
      },
      file: (message: MessageEnvelope) => {
        onNewFileMessage(message);
      },
    };
    pubnubClient.addListener(listener);
    pubnubClient.subscribe({
      channels: subscribedChannels,
      withPresence: false,
    });

    return () => {
      pubnubClient.removeListener(listener);
      // Targeted unsubscribe — `unsubscribeAll` would also wipe out the
      // channel-group subscription owned by `ChatRealtimeListener` and any
      // subscription managed by the @pubnub/react-chat-components <Chat>.
      pubnubClient.unsubscribe({ channels: subscribedChannels });
    };
  }, [pubnubClient, channelsKey, onNewChannelMessage, onNewFileMessage]);
}
